<template>
  <div>
    <div
        class="notice"
        :class="[platform, type, { 'has-image': image, 'readed': readed }]"
        @click="onClick"
    >
      <div class="content">
        <div class="closer">

        </div>
        <div class="header">
          <div class="app-info">
            <div
                v-if="appIcon"
                class="app-icon flex items-center justify-center"
                :class="type"
            >
              <img :src="appIcon"/>
            </div>
            <span class="app-name">{{ appName }}</span>
            <span class="time">{{ timeComputed }}</span>
          </div>
        </div>
        <div class="body">
          <div class="title">{{ title }}</div>
          <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
          <div v-if="message" class="message">{{ message }}</div>
          <div v-if="image" class="image">
            <img :src="image" alt="Notification image"/>
          </div>
        </div>
        <div v-if="actions?.length" class="actions">
          <slot name="actions">
            <q-btn
                v-for="(action, index) in actions"
                :key="index"
                flat
                :label="action.label"
                @click.stop="action.handler"
                class="action-btn"
            />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">

import {MenuItem} from "@/gql/gen";
import {useAction} from "@/core/store/action";
import {computed, toRefs} from "vue";
import {QBtn} from "quasar";
import {NotificationAction} from "@/components/Ui/Notification.vue";
import {formatTime} from "@/core/util/date/formatTime";

const {runAction} = useAction()

const props = withDefaults(defineProps<{
  platform?: 'ios' | 'android';
  appIcon?: any;
  appName?: string;
  time?: number;
  title: string;
  subtitle?: string;
  message?: string;
  image?: string | null;
  type?: 'message' | 'social' | 'email' | 'system' | 'default';
  actions?: NotificationAction[];
  readed?: boolean;
}>(), {
  actions: () => []
})

const {} = toRefs(props)

const emits = defineEmits<{
  closeToast: []
}>()

const timeComputed = computed(() => {
  if (props.time) {
    return formatTime({
      value: props.time,
      dateFormat: 'DD MMMM YYYY',
      timeFormat: 'HH:mm',
      dateTodaySkip: true
    }).datetimeFormatted
  }
});

const onActionClick = (action: Partial<MenuItem>) => {
  runAction(action)
  emits('closeToast')
}

const onClick = () => {

  console.log('CLICK')
}

</script>
<style lang="scss" scoped>
.notice {
  font-family: Montserrat;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid $primary;

  .content {
    padding: 14px 16px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .app-info {
      display: flex;
      align-items: center;
      flex-grow: 1;
      flex-wrap: nowrap;

      .app-icon {
        margin-right: 10px;
        font-weight: bold;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        line-height: 0;
        font-size: 0;
        background-color: $primary;
        overflow: hidden;

        img {
          width: 50%;
        }

        &.message {
          background: #128C7E;
        }

        &.social {
          background: #1877F2;
        }

        &.email {
          background: #EA4335;
        }

        &.system {
          background: #8E44AD;
        }

        &.default {
          background: $primary;
        }
      }

      .app-name {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        flex-grow: 1;
      }

      .time {
        font-size: 13px;
        color: #9E9E9E;
        margin-left: 8px;
      }
    }

    .more-btn {
      color: #9E9E9E;
    }
  }

  .body {
    .title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 4px;
      color: #212121;
    }

    .subtitle {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 4px;
      color: #424242;
    }

    .message {
      font-size: 14px;
      color: #616161;
      line-height: 1.4;
    }

    .image {
      margin-top: 12px;
      border-radius: 8px;
      overflow: hidden;

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    gap: 16px;

    .action-btn {
      font-size: 14px;
      font-weight: 500;
      padding: 6px 12px;
      min-height: auto;

      &:deep(.q-btn__content) {
        text-transform: none;
      }
    }
  }

  // Стили для разных типов уведомлений
  &.message {
    border-left: 4px solid #128C7E;
  }

  &.social {
    border-left: 4px solid #1877F2;
  }

  &.email {
    border-left: 4px solid #EA4335;
  }

  &.system {
    border-left: 4px solid #8E44AD;
  }

  &.default {
    border-left: 4px solid $primary;
  }

  // Специфические стили для Android
  &.android {
    .header {
      .app-info {
        .app-name {
          font-weight: 600;
        }
      }
    }

    .actions {
      .action-btn {
        text-transform: uppercase;
        font-weight: 600;
      }
    }
  }

  // Специфические стили для iOS
  &.ios {
    .header {
      .app-info {
        .app-name {
          font-weight: 500;
        }
      }
    }

    .actions {
      .action-btn {
        color: #007AFF;
        font-weight: 600;
      }
    }
  }
}
</style>
<style lang="scss">

.notice-toast {
  background-color: transparent !important;
  padding: 0 !important;
  box-shadow: none;
}

.closer {

}

</style>
